import { cn } from '@commenda/utils';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as React from 'react';

export interface ProgressInterface
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  accent?:
    | 'teal'
    | 'brick'
    | 'default'
    | 'success'
    | 'warn'
    | 'blue'
    | 'error'
    | 'accent-avocado'
    | 'indigo';
  height?: number;
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressInterface
>(({ className, value, accent = 'default', height, ...props }, ref) => {
  const colors = { secondary: 'accent-teal-10', primary: 'accent-100' };

  const COLORS = ['bg-accent-avocado-10', 'bg-accent-avocado-100'];

  switch (accent) {
    case 'brick':
      colors.primary = 'bg-accent-brick-100';
      colors.secondary = 'bg-accent-brick-10';
      break;
    case 'blue':
      colors.primary = 'bg-[#1177E3]';
      colors.secondary = 'bg-[#E7F1FC]';
      break;
    case 'success':
      colors.primary = 'bg-fg-success';
      colors.secondary = 'bg-bg-tertiary';
      break;
    case 'error':
      colors.primary = 'bg-utility-error-400';
      colors.secondary = 'bg-utility-error-100';
      break;
    case 'warn':
      colors.primary = 'bg-utility-warning-400';
      colors.secondary = 'bg-utility-warning-100';
      break;
    case 'accent-avocado':
      colors.primary = 'bg-accent-avocado-100';
      colors.secondary = 'bg-accent-avocado-10';
      break;
    case 'indigo':
      colors.primary = 'bg-utility-indigo-500';
      colors.secondary = 'bg-bg-tertiary';
      break;
    default:
      colors.primary = 'bg-utility-gray-400';
      colors.secondary = 'bg-utility-gray-200';
  }

  // Don't overflow the scrollbar if the passed-in value is greater than 100%.
  const valueNum = value || 0;
  const displayValue = Math.min(100, valueNum);

  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        'relative h-2 w-full overflow-hidden',
        className,
        colors.secondary
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={(cn('w-full flex-1 transition-all'), colors.primary)}
        style={{
          transform: `translateX(-${100 - (displayValue || 0)}%)`,
          height: height ? `${height}px` : '8px',
          transition: 'all 0.3s ease',
        }}
      />
    </ProgressPrimitive.Root>
  );
});
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
